import { useNavigate, useParams, useLocation } from 'react-router-dom'
import { Divider } from '@mui/material'
import { useState } from 'react'
import { v4 as uuidv4 } from 'uuid'
import { useTranslation } from 'react-i18next'
import Tile from '@/components/Tile/Tile'
import Grid from '@/config/Grid/Grid'
import OrderTile from '@/components/OrderTile/OrderTile'
import styles from './DishConfiguration.module.scss'
import { useDishConfigurationQuery } from './hooks/useDishConfigurationQuery'
import {
  primaryColors,
  whiteShades
} from '@/assets/styles/helpers/colors.styles'
import IconButton from '@/components/IconButton/IconButton'
import { useDishConfigurationStore } from './hooks/useDishConfigurationStore'
import Button from '@/components/Button/Button'
import DishSummaryTile from './components/DishSummaryTile/DishSummaryTile'
import TextField from '@/components/TextField/TextField'
import useTablesStore from '../TablesList/hooks/useTablesStore'
import useOrderPageStore from '../OrderPage/hooks/useOrderPageStore'

function DishConfiguration() {
  const { dishId } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const { t } = useTranslation()
  const editIds: string[] | undefined = location.state?.editIds
  const editCount: number | undefined = location.state?.editCount
  const { dish, dishSizesList, toppingsList } = useDishConfigurationQuery(
    dishId!
  )
  const mappedDishSizesList = dishSizesList
    ?.filter(size => (size.id ? dish?.dishSizeIds?.includes(size.id) : false))
    .map(translation => ({
      id: translation.id,
      title:
        translation.translations.find(t => t.languageCode === 'en')?.name || ''
    }))

  const mappedDishToppingsList = toppingsList
    ?.filter(topping => dish?.dishToppingIds?.includes(topping.id!))
    .map(translation => ({
      id: translation.id,
      title:
        translation.translations.find(t => t.languageCode === 'en')?.name || ''
    }))

  const [toppingMode, setToppingMode] = useState<'add' | 'remove'>('add')

  const handleAddButtonClick = () => {
    setToppingMode('add')
  }

  const handleNoButtonClick = () => {
    setToppingMode('remove')
  }

  const {
    dishSizeId,
    dishToppingIds,
    removedToppingIds,
    note,
    discount,
    guestNumber,
    setDishSizeId,
    setDishToppingIds,
    setRemovedToppingIds,
    setNote,
    setDiscount,
    reset
  } = useDishConfigurationStore()

  const { selectedTableNumber, selectedGuestsNumber, selectedTableId } =
    useTablesStore()

  const dishHasSizes = (dish?.dishSizeIds?.length ?? 0) > 0
  const dishHasToppings = (dish?.dishToppingIds?.length ?? 0) > 0
  const sizeStepDone = !dishHasSizes || !!dishSizeId

  const shouldDisplaySizeTiles = dishHasSizes && !dishSizeId

  const shouldDisplayToppingSection = dishHasToppings && sizeStepDone
  const shouldDisplayToppingTiles = shouldDisplayToppingSection

  const selectedDishSize = mappedDishSizesList?.find(
    size => size.id === dishSizeId
  )

  const dishSizeChips = [
    {
      id: dishSizeId || '',
      label: selectedDishSize?.title || '',
      onDelete: () => {
        setDishSizeId(null)
        setToppingMode('add')
      }
    }
  ].filter(chip => chip.id)

  const dishToppingsChips = dishToppingIds.map(toppingId => {
    const selectedTopping = mappedDishToppingsList?.find(
      topping => topping.id === toppingId
    )
    return {
      id: toppingId,
      label: selectedTopping?.title || '',
      onDelete: () =>
        setDishToppingIds(dishToppingIds.filter(id => id !== toppingId))
    }
  })

  const removedToppingsChips = removedToppingIds.map(toppingId => {
    const ingredient = dish?.ingredients?.find(i => i.id === toppingId)
    const label = ingredient
      ? ingredient.translations.find(t => t.languageCode === 'en')?.name || ''
      : mappedDishToppingsList?.find(t => t.id === toppingId)?.title || ''
    return {
      id: toppingId,
      label,
      onDelete: () =>
        setRemovedToppingIds(removedToppingIds.filter(id => id !== toppingId))
    }
  })

  const { addDish, removeDishes } = useOrderPageStore()

  const handleSubmit = () => {
    if (!dish?.id || !dishSizeId) {
      console.error('Dish ID is undefined')
      return
    }

    const payload = {
      id: uuidv4(),
      dishId: dish.id,
      sizeId: dishSizeId,
      guestNumber: Number(guestNumber),
      toppings: dishToppingIds.map(id => ({ id, quantity: 1 })),
      extraNote: note
    }

    if (editIds?.length) {
      removeDishes(editIds)
    }

    for (let i = 0; i < dishCount; i++) {
      addDish({ ...payload, id: uuidv4() })
    }
    reset()
    navigate(`/table/${selectedTableId}`)
  }

  const handleGoBack = () => {
    reset()
    navigate(`/table/${selectedTableId}`)
  }

  const [dishCount, setDishCount] = useState(editCount ?? 1)

  return (
    <Grid>
      <div className={`start-1 col-7 ${styles.orderPageButtonsTileWrapper}`}>
        <Tile color="black" customPadding="1rem">
          <div className={styles.dishConfigurationHeader}>
            <p className={`title-bold-large ${styles.dishConfigurationTitle}`}>
              {dish?.translations.find(t => t.languageCode === 'en')?.name ||
                ''}
            </p>
            <IconButton
              icon="las la-times icon-large"
              color="primary"
              variant="contained"
              onClick={handleGoBack}
            />
          </div>
          {shouldDisplayToppingSection && (
            <div className={styles.dishConfigurationToppingsButtonsContainer}>
              <Button
                label={t('DISH_CONFIGURATION.ADD')}
                variant="contained"
                color="primary"
                onClick={handleAddButtonClick}
                mobile
                width="10rem"
                disabled={toppingMode === 'add'}
              />
              <Button
                label={t('DISH_CONFIGURATION.NO')}
                variant="contained"
                color="secondary"
                onClick={handleNoButtonClick}
                mobile
                width="10rem"
                disabled={toppingMode === 'remove'}
              />
            </div>
          )}

          <Divider
            sx={{ backgroundColor: primaryColors.white, margin: '0.5rem' }}
          />

          {shouldDisplaySizeTiles && (
            <div className={styles.dishConfigurationTilesContainer}>
              {mappedDishSizesList?.map(size => (
                <OrderTile
                  key={size.id}
                  title={size.title || t('ORDER_PAGE.NO_TITLE')}
                  backgroundColor={whiteShades.shade1}
                  onClick={() => setDishSizeId(size.id!)}
                  selected={size.id === dishSizeId}
                />
              ))}
            </div>
          )}
          {shouldDisplayToppingTiles && (
            <div className={styles.dishConfigurationTilesContainer}>
              {mappedDishToppingsList?.map(topping => (
                <OrderTile
                  key={topping.id}
                  title={topping.title || t('ORDER_PAGE.NO_TITLE')}
                  backgroundColor={whiteShades.shade1}
                  selected={dishToppingIds.includes(topping.id!)}
                  onClick={() => {
                    if (toppingMode === 'add') {
                      if (!dishToppingIds.includes(topping.id!)) {
                        setDishToppingIds([...dishToppingIds, topping.id!])
                      }
                    } else if (dishToppingIds.includes(topping.id!)) {
                      setDishToppingIds(
                        dishToppingIds.filter(id => id !== topping.id)
                      )
                    }
                  }}
                />
              ))}
            </div>
          )}
        </Tile>
      </div>
      <div className={`start-8 col-5 ${styles.orderPageSummaryTileWrapper}`}>
        <Tile color="white" customPadding="1.5rem">
          <div className={styles.orderPageSummaryTileContainer}>
            <div className={styles.orderPageSummaryTileContent}>
              <div className={styles.orderPageSummaryTileHeader}>
                <p
                  className={`title-bold-large ${styles.orderPageSummaryTileTitle}`}
                >
                  {t('DISH_CONFIGURATION.TABLE', {
                    number: selectedTableNumber
                  })}
                </p>
                <p
                  className={`title-medium ${styles.orderPageSummaryTileSubTitle}`}
                >
                  {t('DISH_CONFIGURATION.GUEST', {
                    number: selectedGuestsNumber
                  })}
                </p>
              </div>
              {(dish?.dishSizeIds?.length ?? 0) > 0 && (
                <DishSummaryTile
                  summaryChips={dishSizeChips}
                  summaryTitle={t('DISH_CONFIGURATION.SIZE')}
                />
              )}
              {(dish?.dishToppingIds?.length ?? 0) > 0 && (
                <DishSummaryTile
                  summaryChips={dishToppingsChips}
                  summaryTitle={t('DISH_CONFIGURATION.TOPPINGS')}
                />
              )}
              {removedToppingsChips.length > 0 && (
                <DishSummaryTile
                  summaryChips={removedToppingsChips}
                  summaryTitle={t('DISH_CONFIGURATION.REMOVED')}
                />
              )}
              <TextField
                multiline
                label={t('DISH_CONFIGURATION.NOTE_LABEL')}
                placeholder={t('DISH_CONFIGURATION.NOTE_PLACEHOLDER')}
                focused
                value={note}
                onChange={e => setNote(e.target.value)}
              />
              <TextField
                label={t('DISH_CONFIGURATION.DISCOUNT_LABEL')}
                placeholder={t('DISH_CONFIGURATION.DISCOUNT_PLACEHOLDER')}
                focused
                value={discount > 0 ? String(discount) : ''}
                onChange={e => setDiscount(Number(e.target.value))}
              />
            </div>

            <div className={styles.orderPageSummaryTileButtonsContainer}>
              <div className={styles.orderPageDishCountSection}>
                <IconButton
                  icon="las la-minus"
                  color="primary"
                  variant="contained"
                  onClick={() => setDishCount(count => Math.max(1, count - 1))}
                />
                <p className={`title-bold-large ${styles.orderPageDishCount}`}>
                  {dishCount}
                </p>
                <IconButton
                  icon="las la-plus"
                  color="primary"
                  variant="contained"
                  onClick={() => setDishCount(count => count + 1)}
                />
              </div>
              <Button
                label={t('DISH_CONFIGURATION.CONFIRM')}
                variant="contained"
                color="primary"
                onClick={handleSubmit}
                mobile
                width="100%"
              />
            </div>
          </div>
        </Tile>
      </div>
    </Grid>
  )
}
export default DishConfiguration
